import { calcularFactorIPC } from "../utils/ipcCalculations";

const BASE_URL = "https://api.argly.com.ar/api";
const ARGENTINA_DATOS_URL = "https://api.argentinadatos.com/v1";

// ---- CACHE: fetch once, reuse everywhere ----
let _ipcCache = null;
let _ipcCacheTime = 0;
let _iclCache = null;
let _iclCacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Fetch current IPC value
export const fetchIPCActual = async () => {
  const res = await fetch(`${BASE_URL}/ipc`);
  if (!res.ok) throw new Error("Argly IPC no disponible");
  const json = await res.json();
  return json.data;
};

// Fetch IPC history with cache
export const fetchIPCHistorial = async () => {
  const now = Date.now();
  if (_ipcCache && now - _ipcCacheTime < CACHE_TTL) return _ipcCache;

  // Try Argly
  try {
    const res = await fetch(`${BASE_URL}/ipc/history`);
    if (res.ok) {
      const json = await res.json();
      if (json.data?.length) {
        _ipcCache = json.data;
        _ipcCacheTime = now;
        return json.data;
      }
    }
  } catch (e) {
    console.warn("Argly IPC history failed, trying ArgentinaDatos...");
  }

  // Fallback: ArgentinaDatos
  try {
    const res = await fetch(`${ARGENTINA_DATOS_URL}/finanzas/indices/inflacion`);
    if (res.ok) {
      const arr = await res.json();
      const data = arr
        .filter((e) => new Date(e.fecha).getFullYear() >= 2023)
        .map((e) => {
          const d = new Date(e.fecha);
          return { mes: d.getMonth() + 1, anio: d.getFullYear(), valor: Number(e.valor) };
        });
      if (data.length) {
        _ipcCache = data;
        _ipcCacheTime = now;
        return data;
      }
    }
  } catch (e) {
    console.warn("ArgentinaDatos also failed");
  }

  return _ipcCache || [];
};

// Fetch current ICL value
export const fetchICLActual = async () => {
  const res = await fetch(`${BASE_URL}/icl`);
  if (!res.ok) throw new Error("Argly ICL no disponible");
  const json = await res.json();
  return json.data;
};

// Fetch ICL history with cache
export const fetchICLHistorial = async () => {
  const now = Date.now();
  if (_iclCache && now - _iclCacheTime < CACHE_TTL) return _iclCache;

  try {
    const res = await fetch(`${BASE_URL}/icl/history`);
    if (!res.ok) return _iclCache || [];
    const json = await res.json();
    _iclCache = json.data || [];
    _iclCacheTime = now;
    return _iclCache;
  } catch (e) {
    return _iclCache || [];
  }
};

// Calculate ICL adjustment factor between two dates
export const calcularAjusteICL = async (fechaDesde, fechaHasta) => {
  const historial = await fetchICLHistorial();
  if (!historial.length) return null;

  const parseDate = (str) => {
    const [d, m, y] = str.split("/");
    return new Date(Number(y), Number(m) - 1, Number(d));
  };

  const desde = new Date(fechaDesde);
  const hasta = new Date(fechaHasta);

  let iclDesde = null;
  let iclHasta = null;
  let minDiffDesde = Infinity;
  let minDiffHasta = Infinity;

  historial.forEach((entry) => {
    const entryDate = parseDate(entry.fecha);
    const diffDesde = Math.abs(entryDate - desde);
    const diffHasta = Math.abs(entryDate - hasta);
    if (diffDesde < minDiffDesde) { minDiffDesde = diffDesde; iclDesde = entry.valor; }
    if (diffHasta < minDiffHasta) { minDiffHasta = diffHasta; iclHasta = entry.valor; }
  });

  if (!iclDesde || !iclHasta) return null;

  return {
    factor: iclHasta / iclDesde,
    porcentaje: ((iclHasta / iclDesde - 1) * 100).toFixed(1),
    iclDesde,
    iclHasta,
  };
};

// Convenience wrapper: sums IPC of months in [desde, hasta) using the shared
// helper. Projects missing months by averaging the published ones.
export const calcularAjusteIPC = async (desdeAnio, desdeMes, hastaAnio, hastaMes) => {
  const historial = await fetchIPCHistorial();
  if (!historial.length) return null;
  const fechaDesde = new Date(desdeAnio, desdeMes - 1, 1);
  const fechaHasta = new Date(hastaAnio, hastaMes - 1, 1);
  const res = calcularFactorIPC(historial, fechaDesde, fechaHasta);
  if (!res) return null;
  return {
    factor: res.factor,
    porcentaje: res.porcentaje,
    mesesUsados: res.publicados,
    totalMeses: res.totalMeses,
    parcial: res.aproximado,
    aproximado: res.aproximado,
    faltantes: res.faltantes,
  };
};
